import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Tag, Empty, Spin, Descriptions, Typography, Popconfirm, Space, message } from 'antd';
import { PlusOutlined, LoginOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import apiClient from '../../../shared/services/apiClient';
import { userPath } from '../../../shared/config/paths';

const { Title, Text } = Typography;

const STATE = {
    provisioning: ['processing', 'Activating'],
    installing: ['processing', 'Installing'],
    ready: ['success', 'Running'],
    failed: ['error', 'Failed'],
    suspended: ['warning', 'Suspended'],
    removed: ['default', 'Uninstalled'],
};

/** 虚拟主机 → WordPress:WordPress 套餐的站点,后台地址 / 用户名 / 密码(平台保管) */
export default function WordpressSites() {
    const { t } = useTranslation();
    const [sites, setSites] = useState(null);
    const [passwords, setPasswords] = useState({});
    const [busy, setBusy] = useState({});

    const load = useCallback(async () => {
        try {
            const r = await apiClient.get('/api/user/wordpress/sites');
            setSites(r.data?.data?.sites || []);
        } catch (e) {
            setSites((s) => s || []);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    const pending = (sites || []).some((s) => ['provisioning', 'installing'].includes(s.state) || s.password_busy);
    useEffect(() => {
        if (!pending) return undefined;
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [pending, load]);

    const showPassword = async (s) => {
        try {
            const r = await apiClient.get(`/api/user/wordpress/sites/${s.vhost_id}/password`);
            setPasswords((p) => ({ ...p, [s.vhost_id]: r.data?.data?.password }));
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        }
    };

    const resetPassword = async (s) => {
        setBusy((b) => ({ ...b, [s.vhost_id]: true }));
        try {
            await apiClient.post(`/api/user/wordpress/sites/${s.vhost_id}/reset-password`);
            setPasswords((p) => ({ ...p, [s.vhost_id]: undefined }));
            message.success(t('Resetting password…'));
            load();
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy((b) => ({ ...b, [s.vhost_id]: false }));
        }
    };

    const buy = (
        <Link to={userPath('/provision/host_shared?variant=wordpress')}>
            <Button type="primary" icon={<PlusOutlined />}>{t('Buy')}</Button>
        </Link>
    );

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>WordPress</Title>
                {buy}
            </Space>
            {sites === null ? <Spin /> : sites.length === 0 ? <Card><Empty>{buy}</Empty></Card> : sites.map((s) => {
                const [color, label] = STATE[s.state] || ['default', s.state];
                const ins = s.install;
                const ready = s.state === 'ready' && ins;
                return (
                    <Card key={s.subscription_id} style={{ marginBottom: 16 }}
                        title={<Space>{ready ? <a href={ins.site_url} target="_blank" rel="noopener noreferrer">{s.domain}</a> : s.domain}<Tag color={color}>{t(label)}</Tag></Space>}
                        extra={s.vhost_id ? <Link to={userPath(`/host_shared/vhosts/${s.vhost_id}`)}>{t('Manage hosting')}</Link> : null}>
                        {ready ? (
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label={t('Admin URL')}>
                                    <Space wrap>
                                        <Text copyable>{ins.admin_url}</Text>
                                        <Button size="small" icon={<LoginOutlined />} href={ins.admin_url} target="_blank" rel="noopener noreferrer">{t('Open')}</Button>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Username')}><Text copyable>{ins.admin_user}</Text></Descriptions.Item>
                                <Descriptions.Item label={t('Password')}>
                                    <Space wrap>
                                        {passwords[s.vhost_id]
                                            ? <Text copyable code>{passwords[s.vhost_id]}</Text>
                                            : <Button size="small" icon={<EyeOutlined />} disabled={s.password_busy} onClick={() => showPassword(s)}>{t('Show')}</Button>}
                                        <Popconfirm title={t('Generate a new admin password?')} onConfirm={() => resetPassword(s)}>
                                            <Button size="small" icon={<ReloadOutlined />} loading={busy[s.vhost_id] || s.password_busy}>{t('Reset password')}</Button>
                                        </Popconfirm>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Version')}>{ins.version}</Descriptions.Item>
                            </Descriptions>
                        ) : (
                            <Text type="secondary">
                                {s.state === 'failed' ? t('Installation failed. We have been notified and will fix it.')
                                    : s.state === 'removed' ? t('WordPress was uninstalled from this site.')
                                        : s.state === 'suspended' ? t('This site is suspended')
                                            : t('WordPress is being installed. This usually takes a few minutes.')}
                            </Text>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}
